import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { CourseEditorContext, COURSE_MODES } from '../../../../../contexts/CourseEditorContext';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import './ComponentWrapper.scss';

export const ComponentWrapper = ({
    children,
    isSelected,
    onSelect,
    onDelete,
    onDuplicate,
    id,
    component,
    onResize,
    preserveAspectRatio = false,
    wrapperDragProps = {}, // Optional drag props to allow dragging the whole wrapper when selected
    onOpenEditorDrawer, // Function to open the editor drawer
    canvasScale: propCanvasScale = 1 // Scale factor of the canvas (for adjusting resize deltas)
}) => {
    const { i18n, isRtl } = useContext(I18nReactContext);
    const courseEditorContext = useContext(CourseEditorContext);
    const {mode = COURSE_MODES.EDIT} = courseEditorContext || {};

    // Get canvasScale from context with prop fallback for backward compatibility
    const contextCanvasScale = courseEditorContext?.canvasScale;
    const canvasScale = contextCanvasScale !== undefined ? contextCanvasScale : propCanvasScale;
    const [isEditing, setIsEditing] = useState(false);
    const [style, setStyle] = useState({});
    const [isActionMenuOpen, setIsActionMenuOpen] = useState(false);
    // Track mouse position relative to component edges
    const [mousePosition, setMousePosition] = useState({
        top: false,
        right: false,
        bottom: false,
        left: false
    });
    const wrapperRef = useRef(null);
    const actionMenuRef = useRef(null);
    const contentRef = useRef(null);
    const resizingRef = useRef(false);
    const startPosRef = useRef({ x: 0, y: 0 });
    const startSizeRef = useRef({ width: 0, height: 0 });
    const startComponentPosRef = useRef({ x: 0, y: 0 });

    // Initialize style from component props
    useEffect(() => {
        if (component) {
            setStyle({
                width: component.width || 200,
                height: component.height || 'auto',
                fontSize: component.fontSize || 16,
                fontWeight: component.bold ? 'bold' : 'normal',
                fontStyle: component.italic ? 'italic' : 'normal',
                textDecoration: component.underline ? 'underline' : 'none',
                color: component.color || 'inherit'
            });
        }
    }, [component]);


    // Scroll to top when component is deselected
    useEffect(() => {
        // If component is not selected and we have a reference to the content div
        if (!isSelected && contentRef.current) {
            // Scroll to top
            contentRef.current.scrollTop = 0;
        }
    }, [isSelected]);

    // Close the action menu when clicking outside or when deselected
    useEffect(() => {
        if (!isSelected) {
            setIsActionMenuOpen(false);
            return;
        }

        const handleDocumentClick = (e) => {
            if (!actionMenuRef.current) return;
            if (!actionMenuRef.current.contains(e.target)) {
                setIsActionMenuOpen(false);
            }
        };

        document.addEventListener('click', handleDocumentClick);
        return () => document.removeEventListener('click', handleDocumentClick);
    }, [isSelected]);

    // Track mouse position relative to component edges
    useEffect(() => {
        const isViewMode = mode === COURSE_MODES.ADMIN_VIEW || mode === COURSE_MODES.USER_VIEW;
        if (!wrapperRef.current || isViewMode) return;

        const handleMouseMove = (e) => {
            if (resizingRef.current || window.dragInProgress || !wrapperRef.current) return;

            const rect = wrapperRef.current.getBoundingClientRect();
            const edgeThreshold = 30; // Distance from edge to show controls (in pixels)

            // Calculate distances from mouse to each edge
            const distanceTop = Math.abs(e.clientY - rect.top);
            const distanceRight = Math.abs(e.clientX - rect.right);
            const distanceBottom = Math.abs(e.clientY - rect.bottom);
            const distanceLeft = Math.abs(e.clientX - rect.left);

            // Check if mouse is inside the component
            const isInside = 
                e.clientX >= rect.left && 
                e.clientX <= rect.right && 
                e.clientY >= rect.top && 
                e.clientY <= rect.bottom;

            if (isInside) {
                // Update mouse position state based on proximity to edges
                setMousePosition({
                    top: distanceTop < edgeThreshold,
                    right: distanceRight < edgeThreshold,
                    bottom: distanceBottom < edgeThreshold,
                    left: distanceLeft < edgeThreshold
                });
            } else {
                // If mouse is outside the component, hide all controls
                setMousePosition({
                    top: false,
                    right: false,
                    bottom: false,
                    left: false
                });
            }
        };

        // Add event listener to the document
        document.addEventListener('mousemove', handleMouseMove);

        // Clean up
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, [mode]);

    // Handle delete button click
    const handleDelete = (e) => {
        e.stopPropagation();
        if (onDelete) {
            onDelete(id);
        }
    };

    // Handle edit button click
    const handleEditClick = (e) => {
        e.stopPropagation();
        onOpenEditorDrawer(component);
    };

    // Handle duplicate button click
    const handleDuplicate = (e) => {
        e.stopPropagation();
        if (onDuplicate) {
            onDuplicate(id);
        }
    };


    // Use requestAnimationFrame for smoother animations
    const wrapWithRAF = (callback) => {
        let rafId = null;
        let lastArgs = null;

        return function(...args) {
            lastArgs = args;
            if (rafId === null) {
                rafId = requestAnimationFrame(() => {
                    callback.apply(this, lastArgs);
                    rafId = null;
                });
            }
        };
    };

    // Start resizing - supports all 8 directions
    const startResize = (e, direction) => {
        e.stopPropagation();
        e.preventDefault();

        // Set global flag to indicate resizing is in progress
        window.resizeInProgress = true;

        // Set data-resizing attribute directly for immediate visual feedback
        if (wrapperRef.current) {
            wrapperRef.current.setAttribute('data-resizing', 'true');
        }

        // Store the resize direction (n, s, e, w, ne, nw, se, sw)
        resizingRef.current = direction;
        startPosRef.current = { x: e.clientX, y: e.clientY };
        startSizeRef.current = {
            width: wrapperRef.current.offsetWidth,
            height: wrapperRef.current.offsetHeight
        };

        // Store the component's current position
        startComponentPosRef.current = { 
            x: component.x || 0, 
            y: component.y || 0 
        };

        // Use requestAnimationFrame for smoother animations
        const rafMouseMove = wrapWithRAF(handleMouseMove);

        // Add event listeners (mouse, touch, pointer) to reliably track and stop resizing
        document.addEventListener('mousemove', rafMouseMove);
        document.addEventListener('pointermove', rafMouseMove);
        document.addEventListener('touchmove', rafMouseMove, { passive: false });

        // Function to handle end of interaction
        const handlePointerOrMouseUp = () => {
            stopResize();
            document.removeEventListener('mousemove', rafMouseMove);
            document.removeEventListener('pointermove', rafMouseMove);
            document.removeEventListener('touchmove', rafMouseMove, { passive: false });
            document.removeEventListener('mouseup', handlePointerOrMouseUp);
            document.removeEventListener('pointerup', handlePointerOrMouseUp);
            document.removeEventListener('touchend', handlePointerOrMouseUp);
            document.removeEventListener('touchcancel', handlePointerOrMouseUp);
        };

        document.addEventListener('mouseup', handlePointerOrMouseUp);
        document.addEventListener('pointerup', handlePointerOrMouseUp);
        document.addEventListener('touchend', handlePointerOrMouseUp);
        document.addEventListener('touchcancel', handlePointerOrMouseUp);
    };

    // Handle mouse move during resize - supports all 8 directions
    const handleMouseMove = (e) => {
        if (!resizingRef.current) return;

        // Calculate raw deltas in screen coordinates
        const rawDeltaX = e.clientX - startPosRef.current.x;
        const rawDeltaY = e.clientY - startPosRef.current.y;

        // Adjust deltas by canvas scale to convert screen coordinates to canvas coordinates
        const deltaX = rawDeltaX / canvasScale;
        const deltaY = rawDeltaY / canvasScale;
        const direction = resizingRef.current;

        // Initialize with current values
        let newWidth = startSizeRef.current.width;
        let newHeight = startSizeRef.current.height;
        let newX = startComponentPosRef.current.x;
        let newY = startComponentPosRef.current.y;

        // Handle horizontal resizing
        if (direction.includes('e')) {
            // East: width increases, position stays
            newWidth = startSizeRef.current.width + deltaX;
        } else if (direction.includes('w')) {
            // West: width decreases, position moves right
            newWidth = startSizeRef.current.width - deltaX;
            newX = startComponentPosRef.current.x + deltaX;
        }

        // Handle vertical resizing
        if (direction.includes('s')) {
            // South: height increases, position stays
            newHeight = startSizeRef.current.height + deltaY;
        } else if (direction.includes('n')) {
            // North: height decreases, position moves down
            newHeight = startSizeRef.current.height - deltaY;
            newY = startComponentPosRef.current.y + deltaY;
        }

        // Determine minimum and maximum dimensions based on component type and content
        let minWidth = 100;
        let minHeight = 50;
        let maxWidth = 800; // Default max width (canvas width)
        let maxHeight = 450; // Default max height (canvas height)

        // For text components, we might want to ensure there's enough space for the text
        if (component.type === 'text') {
            // Increase minimum size for text components to prevent content overflow
            minWidth = Math.max(minWidth, 120); // Ensure at least 120px width for text
            minHeight = Math.max(minHeight, 60); // Ensure at least 60px height for text

            // If we have font size information, we can make the minimum height relative to font size
            if (component.fontSize) {
                // Ensure height is at least 3 times the font size to fit text comfortably
                minHeight = Math.max(minHeight, component.fontSize * 3);
            }

            // Set maximum size for text components (e.g., 80% of canvas)
            maxWidth = 640; // 80% of 800px
            maxHeight = 360; // 80% of 450px
        }
        // For image components, allow more flexible resizing
        else if (component.type === 'image') {
            minWidth = 50;  // Allow images to be quite small
            minHeight = 50; // Allow images to be quite small

            // Set maximum size for image components (120% of canvas)
            maxWidth = 960; // 120% of 800px
            maxHeight = 540; // 120% of 450px
        }

        // Enforce minimum dimensions
        if (newWidth < minWidth) {
            newWidth = minWidth;
        }

        if (newHeight < minHeight) {
            newHeight = minHeight;
        }

        // Enforce maximum dimensions
        if (newWidth > maxWidth) {
            newWidth = maxWidth;
        }

        if (newHeight > maxHeight) {
            newHeight = maxHeight;
        }

        // Preserve aspect ratio if needed
        if (preserveAspectRatio && startSizeRef.current.height > 0) {
            const aspectRatio = startSizeRef.current.width / startSizeRef.current.height;

            // Store original dimensions before aspect ratio adjustment
            const preAspectWidth = newWidth;
            const preAspectHeight = newHeight;

            // For 'se' direction (bottom-right), we're doing a diagonal resize
            // Use the larger delta to determine the primary dimension
            if (Math.abs(deltaX) > Math.abs(deltaY)) {
                // Width is primary
                newHeight = newWidth / aspectRatio;
            } else {
                // Height is primary
                newWidth = newHeight * aspectRatio;
            }

            // No need to recalculate positions - component should stay in place

            // Re-enforce minimum and maximum dimensions after aspect ratio preservation
            if (newWidth < minWidth) {
                newWidth = minWidth;
                newHeight = newWidth / aspectRatio;
            } else if (newWidth > maxWidth) {
                newWidth = maxWidth;
                newHeight = newWidth / aspectRatio;
            }

            if (newHeight < minHeight) {
                newHeight = minHeight;
                newWidth = newHeight * aspectRatio;
            } else if (newHeight > maxHeight) {
                newHeight = maxHeight;
                newWidth = newHeight * aspectRatio;
            }
        }

        // Apply the changes directly to the DOM for smoother animation
        if (wrapperRef.current) {
            wrapperRef.current.style.width = `${newWidth}px`;
            wrapperRef.current.style.height = `${newHeight}px`;

            // Store the final dimensions and position to apply when resize is complete
            wrapperRef.current.dataset.finalWidth = newWidth;
            wrapperRef.current.dataset.finalHeight = newHeight;
            wrapperRef.current.dataset.finalX = newX;
            wrapperRef.current.dataset.finalY = newY;
        }

        // For west/north resizing, update parent position in real-time
        // This keeps the opposite edge anchored during resize
        if (onResize && (newX !== startComponentPosRef.current.x || newY !== startComponentPosRef.current.y)) {
            onResize(id, newWidth, newHeight, newX, newY);
        }
    };

    // Stop resizing
    const stopResize = () => {
        if (!resizingRef.current) return; // Prevent multiple calls

        resizingRef.current = false;
        // Clear global flag to indicate resizing is no longer in progress
        window.resizeInProgress = false;

        // Get the final dimensions and position from the dataset
        if (wrapperRef.current) {
            // Remove the data-resizing attribute
            wrapperRef.current.removeAttribute('data-resizing');

            // Get the final values
            const finalWidth = parseFloat(wrapperRef.current.dataset.finalWidth);
            const finalHeight = parseFloat(wrapperRef.current.dataset.finalHeight);
            const finalX = parseFloat(wrapperRef.current.dataset.finalX);
            const finalY = parseFloat(wrapperRef.current.dataset.finalY);

            // Update the style state with the final dimensions
            setStyle(prevStyle => ({
                ...prevStyle,
                width: finalWidth,
                height: finalHeight
            }));

            // Notify parent of the final resize with updated position
            if (onResize) {
                onResize(id, finalWidth, finalHeight, finalX, finalY);
            }

            // Apply a subtle animation to indicate the resize operation has completed
            wrapperRef.current.classList.add('resize-complete');
            setTimeout(() => {
                if (wrapperRef.current) {
                    wrapperRef.current.classList.remove('resize-complete');
                }
            }, 300); // Duration of the animation
        }

        // No need to remove event listeners here as they're removed in the handleMouseUp function
    };

    // Handle pointer down to select component immediately (enables drag on first click)
    const handlePointerDown = (e) => {
        // Don't interfere with resize handles
        if (e.target.classList.contains('resize-handle')) return;

        // Select the component on pointer down (not just click)
        // This enables dragging on first long-press
        if (onSelect && !isSelected) {
            onSelect(id);
        }
    };

    return (
        <div
            ref={wrapperRef}
            className={`component-wrapper ${isSelected ? 'selected' : ''} ${isEditing ? 'editing' : ''}`}
            onClick={(e) => {
                e.stopPropagation();
                if (onSelect) {
                    onSelect(id);
                }
            }}
            onDoubleClick={(e) => {
                e.stopPropagation();
                if (mode === "edit") {
                    onOpenEditorDrawer(component);
                }
            }}
            onPointerDown={handlePointerDown}
            {...wrapperDragProps}
            data-resizing={!!resizingRef.current}
            style={{
                width: style.width,
                height: style.height
            }}
        >
            {/* New: compact 3-dots action menu, visible only when selected in edit mode */}
            {mode === "edit" && isSelected && (
                <div className="action-menu-container" ref={actionMenuRef}>
                    <button
                        className="action-dots-button"
                        title={i18n.translate('weezbeeCourse.editor.componentWrapper.openMenu', 'Open menu')}
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsActionMenuOpen((prev) => !prev);
                        }}
                    >
                        <MoreHorizIcon fontSize="small" />
                    </button>
                    {isActionMenuOpen && (
                        <div className={`action-bar ${isRtl ? 'rtl' : 'ltr'}`}>
                            <button 
                                className="action-item"
                                onClick={(e) => { e.stopPropagation(); handleEditClick(e); setIsActionMenuOpen(false); }}
                                title={i18n.translate('weezbeeCourse.editor.componentWrapper.editButton')}
                            >
                                <EditIcon fontSize="small" />
                            </button>
                            <button 
                                className="action-item"
                                onClick={(e) => { e.stopPropagation(); handleDuplicate(e); setIsActionMenuOpen(false); }}
                                title={i18n.translate('weezbeeCourse.editor.componentWrapper.duplicateButton', 'Duplicate component')}
                            >
                                <ContentCopyIcon fontSize="small" />
                            </button>
                            <button 
                                className="action-item danger"
                                onClick={(e) => { e.stopPropagation(); handleDelete(e); setIsActionMenuOpen(false); }}
                                title={i18n.translate('weezbeeCourse.editor.componentWrapper.deleteButton')}
                            >
                                <DeleteIcon fontSize="small" />
                            </button>
                        </div>
                    )}
                </div>
            )}

            {/* Component content with applied styles */}
            <div 
                ref={contentRef}
                className="component-content"
                style={{
                    fontSize: `${style.fontSize}px`,
                    fontWeight: style.fontWeight,
                    fontStyle: style.fontStyle,
                    textDecoration: style.textDecoration,
                    color: style.color
                }}
            >
                {children}
            </div>

            {/* Resize handles - all 8 positions - only shown in edit mode */}
            {mode === "edit" && (
                <>
                    {/* Corner handles */}
                    <div
                        className="resize-handle resize-nw"
                        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'nw'); }}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'nw'); }}
                        title={i18n.translate('weezbeeCourse.editor.componentWrapper.resizeNW', 'Resize from top-left')}
                    />
                    <div
                        className="resize-handle resize-ne"
                        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'ne'); }}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'ne'); }}
                        title={i18n.translate('weezbeeCourse.editor.componentWrapper.resizeNE', 'Resize from top-right')}
                    />
                    <div
                        className="resize-handle resize-sw"
                        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'sw'); }}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'sw'); }}
                        title={i18n.translate('weezbeeCourse.editor.componentWrapper.resizeSW', 'Resize from bottom-left')}
                    />
                    <div
                        className="resize-handle resize-se"
                        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'se'); }}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'se'); }}
                        title={i18n.translate('weezbeeCourse.editor.componentWrapper.resizeSE', 'Resize from bottom-right')}
                    />
                    {/* Edge handles */}
                    <div
                        className="resize-handle resize-n"
                        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'n'); }}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'n'); }}
                        title={i18n.translate('weezbeeCourse.editor.componentWrapper.resizeN', 'Resize from top')}
                    />
                    <div
                        className="resize-handle resize-s"
                        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 's'); }}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 's'); }}
                        title={i18n.translate('weezbeeCourse.editor.componentWrapper.resizeS', 'Resize from bottom')}
                    />
                    <div
                        className="resize-handle resize-w"
                        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'w'); }}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'w'); }}
                        title={i18n.translate('weezbeeCourse.editor.componentWrapper.resizeW', 'Resize from left')}
                    />
                    <div
                        className="resize-handle resize-e"
                        onPointerDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'e'); }}
                        onMouseDown={(e) => { e.stopPropagation(); e.preventDefault(); startResize(e, 'e'); }}
                        title={i18n.translate('weezbeeCourse.editor.componentWrapper.resizeE', 'Resize from right')}
                    />
                </>
            )}
        </div>
    );
};
